import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Building2,
  MapPin,
  Save,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { OuvidoriaCreationService } from "@/services/ouvidoriaCreationService";
import type { DadosAgenteGestor } from "@/services/ouvidoriaCreationService";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";
import { encryptToken } from "@/utils/tokenCrypto";

interface EnderecoOuvidoria {
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
  pais: string;
  ibge: string;
  latitude?: number;
  longitude?: number;
}

interface DadosCNPJ {
  nome: string;
  fantasia: string;
  razao_social: string;
  numero: string;
  cnpj: string;
  situacao: string;
  natureza_juridica: string;
  atividade_principal: Array<{ code: string; text: string }>;
  cep: string;
}

interface OuvidoriaData {
  dadosCNPJ: DadosCNPJ;
  endereco: EnderecoOuvidoria;
  municipio: {
    id: string;
    guid: string;
    nome: string;
    municipioLong: number;
    codigoIBGE: string;
  };
  ouvidoriaData?: {
    nome: string;
    cnpj: string;
    razaoSocial: string;
    latitude?: number;
    longitude?: number;
    naturezaJuridica: string;
    atividadePrincipal?: string;
    associacaoSupervisora?: boolean;
    nomeFantasia: string;
    cidade: string;
    geral: boolean;
    email?: string;
    telefone?: string;
    endereco: EnderecoOuvidoria;
  };
  dadosAgente?: DadosAgenteGestor;
  tokenId?: string | null;
}

interface EtiquetaAPI {
  id: string;
  nome: string;
  tipo: "Problema" | "Evento" | "Sugestao";
}

type ModoCadastro = 'self' | 'admin';

export default function ConfigurarCategoriasPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const modoCadastro: ModoCadastro = (location.state as { modoCadastro?: ModoCadastro } | null)?.modoCadastro === 'admin' ? 'admin' : 'self';
  const isAdmin = modoCadastro === 'admin';
  const state = location.state as OuvidoriaData;
  const isFluxoUnificado = !!state?.dadosAgente && !!state?.ouvidoriaData?.geral && !isAdmin;
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEtiquetas, setIsLoadingEtiquetas] = useState(false);
  const [categoriasSelecionadas, setCategoriasSelecionadas] = useState<
    string[]
  >([]);
  const [naoSeEncaixaNenhuma, setNaoSeEncaixaNenhuma] = useState(false);
  const [etiquetasAPI, setEtiquetasAPI] = useState<EtiquetaAPI[]>([]);
  const [municipioData, setMunicipioData] = useState<{
    id: string;
    guid: string;
    nome: string;
    municipioLong: number;
    codigoIBGE: string;
  } | null>(null);

  useEffect(() => {
    const state = location.state as OuvidoriaData;
    if (!state || !state.dadosCNPJ || !state.endereco || !state.municipio) {
      toast({
        title: "Erro",
        description:
          "Dados não encontrados. Por favor, valide o CNPJ novamente.",
        variant: "destructive",
      });
      navigate(-1);
      return;
    }

    setMunicipioData({
      id: state.municipio.id,
      guid: state.municipio.guid,
      nome: state.municipio.nome,
      municipioLong: state.municipio.municipioLong,
      codigoIBGE: state.municipio.codigoIBGE || "",
    });
  }, [location.state, navigate, toast]);

  // Buscar etiquetas da API quando municipioData for definido
  useEffect(() => {
    if (!municipioData) return;

    const fetchEtiquetas = async () => {
      setIsLoadingEtiquetas(true);
      try {
        const api = new RemoteDataSource(Settings.urlBase);
        const result = await api.get<EtiquetaAPI[]>({
          endpoint: `v1/etiquetas/${municipioData.id}`,
          requiredToken: false,
          headers: { accept: "application/vnd.data.full.v1+json" },
          forceRefresh: true,
        });

        if ("failure" in result) {
          toast({
            title: "Erro",
            description: "Não foi possível carregar as etiquetas.",
            variant: "destructive",
          });
        } else {
          const etiquetas = (result.success.body as EtiquetaAPI[]) || [];
          setEtiquetasAPI(etiquetas);
        }
      } catch (error) {
        toast({
          title: "Erro",
          description: "Não foi possível carregar as etiquetas.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingEtiquetas(false);
      }
    };

    fetchEtiquetas();
  }, [municipioData, toast]);

  const toggleCategoria = (categoriaNome: string) => {
    // Se "Não se encaixa em nenhuma" estiver selecionado, não permite selecionar outras categorias
    if (naoSeEncaixaNenhuma) return;
    
    setCategoriasSelecionadas((prev) =>
      prev.includes(categoriaNome)
        ? prev.filter((nome) => nome !== categoriaNome)
        : [...prev, categoriaNome],
    );
  };

  const toggleNaoSeEncaixaNenhuma = () => {
    const newValue = !naoSeEncaixaNenhuma;
    setNaoSeEncaixaNenhuma(newValue);
    
    // Se marcar "Não se encaixa em nenhuma", limpa todas as outras categorias
    if (newValue) {
      setCategoriasSelecionadas([]);
    }
  };


  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (categoriasSelecionadas.length === 0 && !naoSeEncaixaNenhuma) {
      toast({
        title: "Atenção",
        description: "Selecione pelo menos uma categoria para a ouvidoria.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      if (!municipioData) {
        throw new Error("Dados do município não encontrados");
      }

      const service = new OuvidoriaCreationService();

      // Usar ouvidoriaData do state se disponível, senão construir dos dadosCNPJ
      const ouvidoriaData = state.ouvidoriaData || {
        nome: state.dadosCNPJ.fantasia || state.dadosCNPJ.nome,
        cnpj: state.dadosCNPJ.cnpj,
        razaoSocial: state.dadosCNPJ.razao_social,
        latitude: state.endereco?.latitude,
        longitude: state.endereco?.longitude,
        naturezaJuridica: state.dadosCNPJ.natureza_juridica,
        atividadePrincipal: state.dadosCNPJ.atividade_principal?.[0]?.text,
        nomeFantasia: state.dadosCNPJ.fantasia,
        cidade: municipioData.nome,
        geral: false,
        email: '',
        telefone: '',
        endereco: {
          logradouro: state.endereco.logradouro,
          numero: state.endereco.numero,
          complemento: state.endereco.complemento || undefined,
          bairro: state.endereco.bairro,
          cidade: state.endereco.cidade,
          estado: state.endereco.estado,
          cep: state.endereco.cep,
          pais: state.endereco.pais,
          ibge: municipioData.codigoIBGE || state.endereco.ibge || '',
          latitude: state.endereco?.latitude,
          longitude: state.endereco?.longitude,
        },
      };

      // Adicionar etiquetas selecionadas ou null se "Não se encaixa em nenhuma" estiver marcado
      const ouvidoriaDataComEtiquetas = {
        ...ouvidoriaData,
        etiquetas: naoSeEncaixaNenhuma ? null : categoriasSelecionadas,
      };

      const isGeral = ouvidoriaDataComEtiquetas.geral === true;
      const dadosAgente = state.dadosAgente;
      const tokenId = state.tokenId ?? null;

      // Em modo self, a criação exige o convite validado nesta sessão e um
      // tokenId fresco emitido pelo backend (POST /ouvidorias-gerais/tokens) —
      // sem ele, a requisição sairia sem o comprovante de origem.
      if (!isAdmin && sessionStorage.getItem('conviteOuvidoriaAccess') !== '1') {
        toast({
          title: 'Convite não validado',
          description: 'Acesse pelo link de convite para criar a ouvidoria.',
          variant: 'destructive',
        });
        navigate('/', { replace: true });
        return;
      }
      if (!isAdmin && isGeral && !tokenId) {
        toast({
          title: 'Convite expirado',
          description:
            'Não foi possível validar o convite de criação. Volte e tente novamente.',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      // Fluxo unificado para ouvidoria geral com dados do agente gestor:
      // cria ouvidoria + usuário + agente em uma única chamada, depois navega
      // direto para a página de 2FA sem dialog de link.
      if (isGeral && dadosAgente && !isAdmin) {
        const result = await service.criarOuvidoriaGeralEAgente(
          municipioData.id,
          municipioData.municipioLong,
          ouvidoriaDataComEtiquetas,
          tokenId,
          dadosAgente
        );

        if ('failure' in result) {
          toast({
            title: 'Erro',
            description: result.failure.message || 'Erro ao criar ouvidoria e agente',
            variant: 'destructive',
          });
          setIsLoading(false);
          return;
        }

        sessionStorage.removeItem('conviteOuvidoriaAccess');
        toast({
          title: 'Sucesso',
          description: 'Ouvidoria e agente gestor criados com sucesso!',
        });

        // Navegar direto para 2FA com dados criptografados na URL
        const encryptedData = await encryptToken(JSON.stringify({
          usuarioId: result.success.usuarioId,
          email: result.success.email,
          tokenAcesso: result.success.tokenAcesso,
        }));
        navigate(`/autenticacao-dois-fatores?d=${encodeURIComponent(encryptedData)}`);
        return;
      }

      // Fluxo padrão (admin ou setorial sem agente gestor integrado)
      const result = isAdmin
        ? await service.criarOuvidoriaParaSupervisao(
            municipioData.id,
            ouvidoriaDataComEtiquetas
          )
        : await service.criarOuvidoriaESalvarId(
            municipioData.id,
            municipioData.municipioLong,
            ouvidoriaDataComEtiquetas
          );

      if ('failure' in result) {
        toast({
          title: 'Erro',
          description: result.failure.message || 'Erro ao criar ouvidoria',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      const novaOuvidoriaId = result.success.ouvidoriaId;
      sessionStorage.removeItem('conviteOuvidoriaAccess');
      toast({
        title: 'Sucesso',
        description: 'Ouvidoria criada com sucesso!',
      });

      // Navegar para página de cadastro de agente.
      // Em modo admin, repassamos a ouvidoriaId via state para que a página
      // de cadastro de agente não dependa do AppConfig (que não foi alterado).
      navigate('/cadastro-agente', {
        state: {
          modoCadastro,
          novaOuvidoriaId,
        },
      });
    } catch (err) {
      toast({
        title: 'Erro',
        description: err instanceof Error ? err.message : 'Ocorreu um erro ao salvar a ouvidoria.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-8 px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="gap-2 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <h1 className="font-heading text-3xl font-bold">
            Configurar Ouvidoria
          </h1>
          <p className="text-muted-foreground mt-2">
            Selecione as categorias e etiquetas que sua ouvidoria vai gerenciar.
          </p>
        </div>

        {/* Resumo da Ouvidoria */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Resumo da Ouvidoria</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="font-medium">
                    {state?.dadosCNPJ?.fantasia || state?.dadosCNPJ?.nome}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {state?.dadosCNPJ?.nome}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 mt-0.5 text-muted-foreground" />
                <p className="text-sm">
                  {municipioData?.nome}, {state?.endereco?.estado}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Categorias */}
          <Card>
            <CardHeader>
              <CardTitle>Categorias</CardTitle>
              <CardDescription>
                Selecione as categorias de denúncias que sua ouvidoria vai
                gerenciar.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {/* Categorias da API */}
                {etiquetasAPI.map((categoria) => (
                  <div
                    key={categoria.id}
                    className={`flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors ${
                      naoSeEncaixaNenhuma ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Checkbox
                      id={`categoria-${categoria.id}`}
                      checked={categoriasSelecionadas.includes(categoria.nome)}
                      onCheckedChange={() => toggleCategoria(categoria.nome)}
                      disabled={naoSeEncaixaNenhuma}
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor={`categoria-${categoria.id}`}
                        className={`font-medium cursor-pointer ${
                          naoSeEncaixaNenhuma ? 'cursor-not-allowed' : ''
                        }`}
                      >
                        {categoria.nome}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {categoria.tipo}
                      </p>
                    </div>
                  </div>
                ))}

                {/* Opção "Não se encaixa em nenhuma" */}
                <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                  <Checkbox
                    id="nao-se-encaixa-ninguma"
                    checked={naoSeEncaixaNenhuma}
                    onCheckedChange={toggleNaoSeEncaixaNenhuma}
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor="nao-se-encaixa-ninguma"
                      className="font-medium cursor-pointer"
                    >
                      Não se encaixa em nenhuma categoria
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Minha ouvidoria não se encaixa em nenhuma das categorias listadas acima
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading || (categoriasSelecionadas.length === 0 && !naoSeEncaixaNenhuma)}
            className="w-full h-14 text-base font-medium gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {isFluxoUnificado ? "Criando ouvidoria e agente..." : "Criando ouvidoria..."}
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                {isFluxoUnificado ? "Criar Ouvidoria e Agente Gestor" : "Criar Ouvidoria"}
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
